import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Stack,
  Chip,
  Button,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  alpha,
  useTheme,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ErrorIcon from "@mui/icons-material/Error";
import StorageIcon from "@mui/icons-material/Storage";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import StarIcon from "@mui/icons-material/Star";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import { useLocation, useHistory } from "react-router-dom";
import PageHeader from "../../common/PageHeader";

// ── Gateway option card ────────────────────────────────────────────────────
const GatewayOption = ({
  value,
  selected,
  label,
  icon,
  description,
  color,
}) => {
  const theme = useTheme();
  return (
    <Paper
      variant="outlined"
      onClick={() => {}} // handled by RadioGroup
      sx={{
        p: 2,
        borderRadius: 2,
        cursor: "pointer",
        borderColor: selected ? color : alpha(theme.palette.divider, 0.8),
        borderWidth: selected ? 2 : 1,
        bgcolor: selected ? alpha(color, 0.04) : "transparent",
        transition: "all 0.2s ease",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <FormControlLabel
          value={value}
          control={
            <Radio size="small" sx={{ color, "&.Mui-checked": { color } }} />
          }
          label=""
          sx={{ m: 0 }}
        />
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(color, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box flex={1}>
          <Typography variant="body2" fontWeight={700}>
            {label}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
const MyPlan = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();

  const [profile, setProfile] = useState(null);
  const [plans, setPlans] = useState([]);
  const [gateways, setGateways] = useState({});
  const [offlineSettings, setOfflineSettings] = useState({
    instructions: "",
    details: "",
  });

  // Payment flow state
  const [payDialog, setPayDialog] = useState({ open: false, plan: null });
  const [selectedGateway, setSelectedGateway] = useState("");

  // Offline sub-flow
  const [offlineNotes, setOfflineNotes] = useState("");
  const [offlineSubmitting, setOfflineSubmitting] = useState(false);
  const [offlineSuccess, setOfflineSuccess] = useState(false);

  // Stripe
  const [stripeLoading, setStripeLoading] = useState(false);

  // Post-payment status
  const [statusDialog, setStatusDialog] = useState(null);

  // ── Load data ────────────────────────────────────────────────────────────
  useEffect(() => {
    loadProfile();
    loadPlans();
    loadSettings();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get("payment");
    const plan = params.get("plan");
    if (payment === "success") {
      setStatusDialog({ type: "success", plan });
      loadProfile();
      history.replace("/user");
    } else if (payment === "cancelled") {
      setStatusDialog({ type: "cancelled" });
      history.replace("/user");
    } else if (payment === "error") {
      setStatusDialog({ type: "error" });
      history.replace("/user");
    }
  }, [location.search]);

  const loadProfile = async () => {
    const res = await hitAxios({
      path: "/api/user/profile",
      showSnackbar: false,
    });
    if (res?.data?.success) setProfile(res.data.data);
  };

  const loadPlans = async () => {
    const res = await hitAxios({
      path: "/api/plans/get-all",
      showSnackbar: false,
    });
    if (res?.data?.success)
      setPlans(res.data.data.filter((p) => p.status === "active"));
  };

  const loadSettings = async () => {
    const res = await hitAxios({
      path: "/api/settings/payment-gateway-public",
      showSnackbar: false,
    });
    if (res?.data?.success) {
      const gw = {};
      res.data.data.forEach((g) => {
        gw[g.gateway] = g;
      });
      setGateways(gw);
    }
    const settRes = await hitAxios({
      path: "/api/settings/get-all",
      showSnackbar: false,
    });
    if (settRes?.data?.success)
      setOfflineSettings({
        instructions: settRes.data.data.offline_payment_instructions || "",
        details: settRes.data.data.offline_payment_details || "",
      });
  };

  // ── Gateway flags ────────────────────────────────────────────────────────
  const paypalActive = parseInt(gateways.paypal?.is_active) === 1;
  const stripeActive = parseInt(gateways.stripe?.is_active) === 1;
  const offlineActive = parseInt(gateways.offline?.is_active) === 1;
  const paypalClientId = gateways.paypal?.client_id || "";

  const activeGatewayCount = [paypalActive, stripeActive, offlineActive].filter(
    Boolean,
  ).length;

  // ── Open payment dialog ──────────────────────────────────────────────────
  const openPayDialog = (plan) => {
    setPayDialog({ open: true, plan });
    setOfflineNotes("");
    setOfflineSuccess(false);
    setStripeLoading(false);
    // Auto-select first active gateway
    if (paypalActive) setSelectedGateway("paypal");
    else if (stripeActive) setSelectedGateway("stripe");
    else if (offlineActive) setSelectedGateway("offline");
    else setSelectedGateway("");
  };

  const closePayDialog = () => {
    if (stripeLoading || offlineSubmitting) return;
    setPayDialog({ open: false, plan: null });
    setOfflineSuccess(false);
  };

  // ── PayPal handler ───────────────────────────────────────────────────────
  const handlePayPalApprove = async (orderID, plan) => {
    const res = await hitAxios({
      path: "/api/orders/pay-with-paypal",
      post: true,
      obj: { orderID, plan_id: plan.id },
    });
    if (res?.data?.success) {
      closePayDialog();
      setStatusDialog({ type: "success", plan: plan.name });
      loadProfile();
    }
  };

  // ── Stripe handler ───────────────────────────────────────────────────────
  const handleStripe = async () => {
    const plan = payDialog.plan;
    setStripeLoading(true);
    const res = await hitAxios({
      path: "/api/orders/create-stripe-checkout",
      post: true,
      obj: { plan_id: plan.id },
      showSnackbar: false,
    });
    setStripeLoading(false);
    if (res?.data?.success && res.data.session?.url)
      window.location.href = res.data.session.url;
  };

  // ── Offline handler ──────────────────────────────────────────────────────
  const handleOfflineSubmit = async () => {
    setOfflineSubmitting(true);
    const res = await hitAxios({
      path: "/api/orders/submit-offline",
      post: true,
      obj: { plan_id: payDialog.plan.id, payment_notes: offlineNotes },
    });
    setOfflineSubmitting(false);
    if (res?.data?.success) setOfflineSuccess(true);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const isPlanActive = (planId) =>
    profile?.plan_id === planId &&
    profile?.plan_expires_at &&
    new Date(profile.plan_expires_at) > new Date();

  const isExpired =
    profile?.plan_expires_at && new Date(profile.plan_expires_at) < new Date();

  const planColors = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
  ];

  const handleFreePlan = async (plan) => {
    const res = await hitAxios({
      path: "/api/orders/activate-free",
      post: true,
      obj: { plan_id: plan.id },
    });
    if (res?.data?.success) {
      setStatusDialog({ type: "success", plan: plan.name });
      loadProfile();
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box>
      <PageHeader
        title={lang.myPlan || "My Plan"}
        subtitle="Manage your subscription and upgrade your plan"
        icon={CardMembershipIcon}
      />

      {/* ── Current Plan Banner ── */}
      {profile && (
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: "hidden",
            borderColor: alpha(theme.palette.primary.main, 0.3),
          }}
        >
          <Box
            sx={{
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.4)})`,
            }}
          />
          <Box sx={{ p: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2.5}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.6)})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.35)}`,
                  }}
                >
                  <CardMembershipIcon sx={{ fontSize: 26, color: "#fff" }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      color: "text.secondary",
                      fontSize: "0.68rem",
                    }}
                  >
                    Current Plan
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{ letterSpacing: "-0.02em", color: "text.primary" }}
                  >
                    {profile.plan_name || "Free"}
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
              >
                <Chip
                  label={isExpired ? "Expired" : "Active"}
                  size="small"
                  color={isExpired ? "error" : "success"}
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
                {profile.plan_expires_at && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CalendarTodayIcon
                      sx={{ fontSize: 14, color: "text.secondary" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {isExpired ? "Expired" : "Expires"}:{" "}
                      <Typography
                        component="span"
                        variant="body2"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {new Date(profile.plan_expires_at).toLocaleDateString()}
                      </Typography>
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Box>
        </Paper>
      )}

      {isExpired && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {lang.planExpiredWarning ||
            "Your plan has expired. Upgrade below to continue."}
        </Alert>
      )}

      {/* ── Section Title ── */}
      <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
        <StarIcon sx={{ fontSize: 20, color: theme.palette.warning.main }} />
        <Typography variant="h6" fontWeight={700}>
          {lang.availablePlans || "Available Plans"}
        </Typography>
        <Chip
          label={`${plans.length} plans`}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      {/* ── Plan Cards ── */}
      <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
        {plans.map((plan, idx) => {
          const color = planColors[idx % planColors.length];
          const isActive = isPlanActive(plan.id);
          return (
            <Grid
              size={{ xs: 12, sm: 6, lg: 4 }}
              key={plan.id}
              sx={{ display: "flex" }}
            >
              <Paper
                variant="outlined"
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  overflow: "hidden",
                  borderColor: isActive ? color : alpha(color, 0.18),
                  borderWidth: isActive ? 2 : 1,
                  transition: "all 0.25s ease",
                  position: "relative",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 16px 40px ${alpha(color, 0.2)}`,
                    borderColor: alpha(color, 0.5),
                  },
                }}
              >
                {/* Top accent bar */}
                <Box
                  sx={{
                    height: 4,
                    background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.4)})`,
                  }}
                />

                {/* CURRENT badge */}
                {isActive && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 14,
                      px: 1.5,
                      py: 0.3,
                      borderRadius: 10,
                      bgcolor: alpha(color, 0.12),
                      border: `1px solid ${alpha(color, 0.3)}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={800}
                      sx={{
                        color,
                        fontSize: "0.62rem",
                        letterSpacing: "0.06em",
                      }}
                    >
                      CURRENT
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    p: 3,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Plan name + price */}
                  <Typography variant="subtitle1" fontWeight={700} mb={1}>
                    {plan.name}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="flex-end"
                    spacing={0.5}
                    mb={2}
                  >
                    <Typography
                      variant="h3"
                      fontWeight={800}
                      sx={{ color, letterSpacing: "-0.04em", lineHeight: 1 }}
                    >
                      ${plan.price}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      mb={0.5}
                    >
                      / {plan.duration_days} days
                    </Typography>
                  </Stack>

                  {plan.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mb={2}
                      sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}
                    >
                      {plan.description}
                    </Typography>
                  )}

                  {/* Features */}
                  <Stack spacing={1} mb={3}>
                    {[
                      {
                        icon: <StorageIcon sx={{ fontSize: 15 }} />,
                        label: `${plan.max_storage_gb} GB Storage`,
                      },
                      {
                        icon: <CloudDownloadIcon sx={{ fontSize: 15 }} />,
                        label: `${plan.max_downloads} Downloads`,
                      },
                      {
                        icon: <CalendarTodayIcon sx={{ fontSize: 15 }} />,
                        label: `${plan.duration_days} Days Access`,
                      },
                    ].map((feat) => (
                      <Stack
                        key={feat.label}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            bgcolor: alpha(color, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color,
                            flexShrink: 0,
                          }}
                        >
                          {feat.icon}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          {feat.label}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  <Divider sx={{ mb: 2 }} />

                  {/* CTA Button */}
                  <Box sx={{ mt: "auto" }}>
                    {parseFloat(plan.price) === 0 ? (
                      // ── FREE PLAN ──
                      isActive ? (
                        <Button
                          fullWidth
                          variant="outlined"
                          disabled
                          sx={{
                            borderRadius: 2,
                            fontWeight: 700,
                            py: 1.2,
                            borderColor: color,
                            color,
                          }}
                        >
                          ✓ Current Plan
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => handleFreePlan(plan)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 700,
                            py: 1.2,
                            borderColor: color,
                            color,
                            "&:hover": {
                              borderColor: color,
                              bgcolor: alpha(color, 0.06),
                            },
                          }}
                        >
                          Activate Free Plan
                        </Button>
                      )
                    ) : activeGatewayCount === 0 ? (
                      // ── PAID — no gateway ──
                      <Alert
                        severity="info"
                        sx={{ borderRadius: 2, fontSize: "0.75rem" }}
                      >
                        No payment method enabled. Contact admin.
                      </Alert>
                    ) : (
                      // ── PAID — normal checkout ──
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => openPayDialog(plan)}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 700,
                          py: 1.2,
                          background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.75)})`,
                          boxShadow: `0 4px 14px ${alpha(color, 0.35)}`,
                          "&:hover": {
                            background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.9)})`,
                            boxShadow: `0 6px 20px ${alpha(color, 0.5)}`,
                          },
                        }}
                      >
                        {isActive ? "Renew Plan" : "Get Started"}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* ══════════════════════════════════════════════════════════════════
          PAYMENT METHOD DIALOG
      ══════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={payDialog.open}
        onClose={closePayDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PaymentIcon sx={{ fontSize: 18, color: "primary.main" }} />
            </Box>
            <Box>
              <Typography fontWeight={700}>Choose Payment Method</Typography>
              <Typography variant="caption" color="text.secondary">
                {payDialog.plan?.name} — ${payDialog.plan?.price} /{" "}
                {payDialog.plan?.duration_days} days
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {offlineSuccess ? (
            /* ── Offline success state ── */
            <Stack alignItems="center" py={3} spacing={1.5}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleIcon
                  sx={{ fontSize: 36, color: theme.palette.success.main }}
                />
              </Box>
              <Typography fontWeight={700} variant="h6">
                Submitted!
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Admin will verify and activate your plan shortly.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2.5} mt={0.5}>
              {/* ── Gateway selector ── */}
              <FormControl fullWidth>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 1,
                  }}
                >
                  Select Payment Method
                </Typography>
                <RadioGroup
                  value={selectedGateway}
                  onChange={(e) => setSelectedGateway(e.target.value)}
                >
                  <Stack spacing={1}>
                    {paypalActive && paypalClientId && (
                      <GatewayOption
                        value="paypal"
                        selected={selectedGateway === "paypal"}
                        label="PayPal"
                        description="Pay securely with your PayPal account"
                        icon={
                          <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
                        }
                        color="#0070BA"
                      />
                    )}
                    {stripeActive && (
                      <GatewayOption
                        value="stripe"
                        selected={selectedGateway === "stripe"}
                        label="Credit / Debit Card"
                        description="Visa, Mastercard, Amex and more via Stripe"
                        icon={<CreditCardIcon sx={{ fontSize: 18 }} />}
                        color="#635BFF"
                      />
                    )}
                    {offlineActive && (
                      <GatewayOption
                        value="offline"
                        selected={selectedGateway === "offline"}
                        label="Offline / Manual Payment"
                        description="Bank transfer, UPI, or other manual methods"
                        icon={<ReceiptLongIcon sx={{ fontSize: 18 }} />}
                        color={theme.palette.success.main}
                      />
                    )}
                  </Stack>
                </RadioGroup>
              </FormControl>

              {/* ── PayPal inline buttons (only when selected) ── */}
              {selectedGateway === "paypal" &&
                paypalActive &&
                paypalClientId && (
                  <Box>
                    <Divider sx={{ mb: 2 }} />
                    <PayPalScriptProvider
                      options={{ "client-id": paypalClientId, currency: "USD" }}
                    >
                      <PayPalButtons
                        createOrder={(data, actions) =>
                          actions.order.create({
                            purchase_units: [
                              {
                                description: payDialog.plan?.name,
                                amount: {
                                  value: String(payDialog.plan?.price),
                                },
                              },
                            ],
                          })
                        }
                        onApprove={async (data, actions) => {
                          const order = await actions.order.capture();
                          handlePayPalApprove(order.id, payDialog.plan);
                        }}
                        style={{
                          color: "blue",
                          layout: "horizontal",
                          height: 44,
                          tagline: false,
                          shape: "rect",
                        }}
                      />
                    </PayPalScriptProvider>
                  </Box>
                )}

              {/* ── Offline notes (only when selected) ── */}
              {selectedGateway === "offline" && (
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      {offlineSettings.instructions}
                    </Alert>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                        display="block"
                        mb={1}
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        Payment Details
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-line",
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                          color: "text.primary",
                        }}
                      >
                        {offlineSettings.details}
                      </Typography>
                    </Paper>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      label="Transaction Reference / Notes (optional)"
                      placeholder="e.g. Transaction ID, UTR number..."
                      value={offlineNotes}
                      onChange={(e) => setOfflineNotes(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={closePayDialog} sx={{ borderRadius: 2 }}>
            {offlineSuccess ? "Close" : "Cancel"}
          </Button>

          {/* Stripe CTA */}
          {!offlineSuccess && selectedGateway === "stripe" && (
            <Button
              variant="contained"
              onClick={handleStripe}
              disabled={stripeLoading}
              startIcon={
                stripeLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CreditCardIcon />
                )
              }
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                bgcolor: "#635BFF",
                "&:hover": { bgcolor: "#4F46E5" },
              }}
            >
              {stripeLoading ? "Redirecting..." : "Pay with Card"}
            </Button>
          )}

          {/* Offline submit CTA */}
          {!offlineSuccess && selectedGateway === "offline" && (
            <Button
              variant="contained"
              onClick={handleOfflineSubmit}
              disabled={offlineSubmitting}
              startIcon={
                offlineSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <ReceiptLongIcon />
                )
              }
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                bgcolor: theme.palette.success.main,
                "&:hover": { bgcolor: theme.palette.success.dark },
              }}
            >
              {offlineSubmitting ? "Submitting..." : "Submit Payment"}
            </Button>
          )}

          {/* PayPal: no button here — PayPal renders its own above */}
          {!offlineSuccess && selectedGateway === "paypal" && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ alignSelf: "center" }}
            >
              Use the PayPal button above to complete payment
            </Typography>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Post-payment Status Dialog ── */}
      <Dialog
        open={!!statusDialog}
        onClose={() => setStatusDialog(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ textAlign: "center", py: 5, px: 4 }}>
          {statusDialog?.type === "success" && (
            <Stack alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleIcon
                  sx={{ fontSize: 44, color: theme.palette.success.main }}
                />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  Payment Successful! 🎉
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{statusDialog.plan}</strong> plan has been activated.
                </Typography>
              </Box>
            </Stack>
          )}

          {(statusDialog?.type === "cancelled" ||
            statusDialog?.type === "error") && (
            <Stack alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ErrorIcon
                  sx={{ fontSize: 44, color: theme.palette.error.main }}
                />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  {statusDialog?.type === "cancelled"
                    ? "Payment Cancelled"
                    : "Something went wrong"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {statusDialog?.type === "cancelled"
                    ? "No charges were made. You can try again anytime."
                    : "Contact support if you were charged."}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={() => setStatusDialog(null)}
            sx={{ px: 5, borderRadius: 2, fontWeight: 700 }}
          >
            {statusDialog?.type === "success" ? "Done 🎉" : "Close"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyPlan;
